/**
 * ReadyStatusIndicator Component
 *
 * Displays the ready status of both players during the setup phase.
 * Shows visual feedback like "1/2 players ready" or "Opponent is ready".
 *
 * Props:
 * - player1Ready: boolean - Whether player 1 is ready
 * - player2Ready: boolean - Whether player 2 is ready
 * - playerId: string - Current player's ID ('player1' or 'player2')
 * - gamePhase: string - Current game phase
 *
 * Design:
 * - Shows during setup and waiting phases
 * - Hidden during playing and ended phases
 * - Clear visual indication of readiness state
 * - Updates in real-time via WebSocket state changes
 */

import PropTypes from 'prop-types';
import { COPY, getPlayerLabel } from '../copy';

function ReadyStatusIndicator({
  player1Ready,
  player2Ready,
  playerId,
  gamePhase,
}) {
  // Don't show indicator during playing or ended phases
  if (gamePhase === 'playing' || gamePhase === 'ended') {
    return null;
  }

  // Calculate ready count
  const readyCount = (player1Ready ? 1 : 0) + (player2Ready ? 1 : 0);

  // Determine if opponent is ready
  const isPlayer1 = playerId === 'player1';
  const opponentReady = isPlayer1 ? player2Ready : player1Ready;
  const myReady = isPlayer1 ? player1Ready : player2Ready;

  // Don't show anything if no one is ready yet
  if (readyCount === 0) {
    return null;
  }

  return (
    <div
      className="bg-dark-purple border-2 border-neon-cyan shadow-glow-cyan rounded-lg p-4 mb-4"
      data-testid="ready-status-indicator"
      role="status"
      aria-live="polite"
    >
      <div className="flex items-center justify-center gap-3">
        {/* Ready count badge */}
        <div className="flex items-center gap-2">
          <span className="text-2xl font-bold text-neon-cyan text-glow-cyan">
            {readyCount}/2
          </span>
          <span className="text-sm text-gray-300">
            {readyCount === 1 ? 'player ready' : 'players ready'}
          </span>
        </div>

        {/* Visual separator */}
        <div className="h-8 w-px bg-neon-cyan/30"></div>

        {/* Status message */}
        <div className="text-sm">
          {myReady && opponentReady && (
            <span className="text-neon-green text-glow-cyan font-semibold">
              ✓ {COPY.ready.bothReady}
            </span>
          )}
          {myReady && !opponentReady && (
            <span className="text-neon-cyan">
              ⏳ {COPY.ready.waitingForOpponent}
            </span>
          )}
          {!myReady && opponentReady && (
            <span className="text-neon-yellow text-glow-yellow">
              ⚠️ {COPY.ready.opponentReady}
            </span>
          )}
        </div>
      </div>

      {/* Individual player status (optional, for clarity) */}
      {readyCount > 0 && (
        <div className="mt-3 flex justify-center gap-4 text-xs text-gray-400">
          <div className="flex items-center gap-1">
            <span
              className={`inline-block w-3 h-3 rounded-full ${
                player1Ready ? 'bg-neon-green shadow-glow-green' : 'bg-gray-600'
              }`}
              aria-label={
                player1Ready ? `${getPlayerLabel('player1')} ready` : `${getPlayerLabel('player1')} not ready`
              }
            ></span>
            <span>{getPlayerLabel('player1')}</span>
          </div>
          <div className="flex items-center gap-1">
            <span
              className={`inline-block w-3 h-3 rounded-full ${
                player2Ready ? 'bg-neon-green shadow-glow-green' : 'bg-gray-600'
              }`}
              aria-label={
                player2Ready ? `${getPlayerLabel('player2')} ready` : `${getPlayerLabel('player2')} not ready`
              }
            ></span>
            <span>{getPlayerLabel('player2')}</span>
          </div>
        </div>
      )}
    </div>
  );
}

ReadyStatusIndicator.propTypes = {
  player1Ready: PropTypes.bool,
  player2Ready: PropTypes.bool,
  playerId: PropTypes.string,
  gamePhase: PropTypes.string.isRequired,
};

ReadyStatusIndicator.defaultProps = {
  player1Ready: false,
  player2Ready: false,
  playerId: null,
};

export default ReadyStatusIndicator;
